import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useUpdateUserMutation } from "../redux/Apis/User";
import {
  createPageThemeVars,
  getStoredThemeKey,
  getThemeByKey,
  isValidThemeKey,
  setStoredThemeKey,
} from "../utils/theme";

// Logged-in users get their theme persisted per-user via the update user API.
// Guests keep the existing localStorage based preference.
const usePageTheme = () => {
  const { user, isLoggedIn } = useSelector((state) => state.authSlice);
  const [updateUser] = useUpdateUserMutation();

  const [selectedTheme, setSelectedThemeState] = useState(() =>
    isLoggedIn && isValidThemeKey(user?.theme)
      ? user.theme
      : getStoredThemeKey(),
  );

  useEffect(() => {
    if (isLoggedIn && isValidThemeKey(user?.theme)) {
      setSelectedThemeState(user.theme);
    }
  }, [isLoggedIn, user?.theme]);

  const setSelectedTheme = (themeKey) => {
    setSelectedThemeState(themeKey);

    if (isLoggedIn && user?._id) {
      updateUser({ id: user._id, payload: { theme: themeKey } })
        .unwrap()
        .then(() => {
          toast.success("Theme preference saved.");
        })
        .catch(() => {
          toast.error("Failed to save theme preference. Try again.");
        });
      return;
    }

    setStoredThemeKey(themeKey);
  };

  const currentTheme = useMemo(() => getThemeByKey(selectedTheme), [selectedTheme]);
  const pageThemeVars = useMemo(
    () => createPageThemeVars(currentTheme),
    [currentTheme],
  );

  return { selectedTheme, setSelectedTheme, currentTheme, pageThemeVars };
};

export default usePageTheme;
